import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const CERT = ["NOT APPLIED", "NOT CERTIFIED", "TRAINING REQUIRED", "TRAINING IN PROGRESS", "ASSESSMENT PENDING", "AWAITING CERTIFICATION", "CERTIFIED", "SUSPENDED", "EXPIRED"];
const LEVELS = ["ASSOCIATE", "CERTIFIED", "PRO", "MASTER", "REGIONAL"];

const DETAIL_FIELDS = [
  ["applicator_code", "Applicator ID"], ["mobile", "Mobile"], ["whatsapp", "WhatsApp"], ["email", "Email"],
  ["city", "City"], ["pincode", "PIN"], ["address", "Address"], ["current_business", "Business"],
  ["profession", "Profession"], ["experience_years", "Experience (yrs)"], ["technicians", "Technicians"],
  ["service_area", "Service Area"], ["referral_code", "Referral Code"], ["status_reason", "Status Reason"],
];

export default function AdminApplicators() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/applicators", { params: filter ? { status: filter } : {} }).then(({ data }) => setApps(data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (aid, body) => { await api.put(`/admin/applicators/${aid}/status`, body); toast.success("Updated"); load(); };
  const withReason = async (aid, status) => {
    const reason = window.prompt(`Reason for ${status} (recorded in the audit log):`, "");
    if (reason === null) return;
    await setStatus(aid, { status, reason });
  };
  const certify = async (aid, service_id, status) => {
    await api.put(`/admin/applicators/${aid}/services/${service_id}/certification`, { status });
    toast.success("Certification updated"); 
    const { data } = await api.get("/admin/applicators", { params: filter ? { status: filter } : {} });
    setApps(data); setOpen(data.find((x) => x.applicator_id === aid));
  };

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="admin-applicators-page">
      <Seo title="Admin · Applicators" description="Manage ERRĀH applicators" />
      <div className="glass"><div className="max-w-[1200px] mx-auto px-5 h-[60px] flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2 text-sm text-gray-300"><ArrowLeft size={16} /> Admin</Link>
        <div className="flex items-center gap-4">
          <Link to="/admin/analytics" className="text-sm text-[#C9A45C]">Analytics →</Link>
          <Link to="/admin/training" className="text-sm text-[#C9A45C]">Training →</Link>
          <Link to="/admin/applicator-services" className="text-sm text-[#C9A45C]">Applicator Services →</Link>
        </div>
      </div></div>

      <div className="max-w-[1200px] mx-auto px-5 py-10">
        <h1 className="serif-head text-3xl lg:text-4xl mb-6">Applicators</h1>
        <div className="flex gap-2 mb-6" data-testid="applicator-filter">
          {["", "pending", "approved", "rejected", "suspended"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs border ${filter === s ? "border-[#C9A45C] text-[#C9A45C]" : "border-white/15 text-gray-400"}`}>{s || "all"}</button>
          ))}
        </div>

        <div className="space-y-3">
          {apps.map((a) => (
            <div key={a.applicator_id} className="hair-border bg-white/[0.02] p-4" data-testid={`admin-app-${a.applicator_code}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg">{a.name} <span className="text-xs gold-text">{a.applicator_code}</span></p>
                  <p className="text-xs text-gray-500">{a.city} · {a.mobile} · {a.status} · {a.level}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setDetail(detail === a.applicator_id ? null : a.applicator_id)} data-testid={`view-${a.applicator_code}`} className="px-3 py-1.5 text-xs border border-white/20 text-gray-300">View</button>
                  <button onClick={() => setStatus(a.applicator_id, { status: "approved" })} data-testid={`approve-${a.applicator_code}`} className="px-3 py-1.5 text-xs bg-emerald-600 text-white">Approve</button>
                  <button onClick={() => withReason(a.applicator_id, "rejected")} data-testid={`reject-${a.applicator_code}`} className="px-3 py-1.5 text-xs border border-red-500/50 text-red-400">Reject</button>
                  <button onClick={() => withReason(a.applicator_id, "suspended")} data-testid={`suspend-${a.applicator_code}`} className="px-3 py-1.5 text-xs border border-white/20 text-gray-300">Suspend</button>
                  <select value={a.level} onChange={(e) => setStatus(a.applicator_id, { level: e.target.value })} className="bg-[#111] border border-white/15 px-2 py-1.5 text-xs">
                    {LEVELS.map((l) => <option key={l}>{l}</option>)}
                  </select>
                  <button onClick={() => setOpen(open?.applicator_id === a.applicator_id ? null : a)} className="px-3 py-1.5 text-xs border border-[#C9A45C]/50 text-[#C9A45C]">Services ({a.services?.length || 0})</button>
                </div>
              </div>

              {detail === a.applicator_id && (
                <div className="mt-4 pt-4 border-t border-white/10 grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2" data-testid={`applicant-detail-${a.applicator_code}`}>
                  {DETAIL_FIELDS.map(([k, label]) => (
                    <div key={k}><span className="errah-label !text-[9px] block">{label}</span><span className="text-sm text-gray-300">{a[k] || "—"}</span></div>
                  ))}
                  <div><span className="errah-label !text-[9px] block">Application Date</span><span className="text-sm text-gray-300">{(a.created_at || "").slice(0, 10)}</span></div>
                  <div className="sm:col-span-2 md:col-span-3"><span className="errah-label !text-[9px] block mb-1">Services & experience</span>
                    <span className="text-sm text-gray-300">{(a.services || []).map((s) => `${s.service_name} (${s.experience_level || "—"}, ${s.years || 0}y)`).join(", ") || "—"}</span></div>
                </div>
              )}

              {open?.applicator_id === a.applicator_id && (
                <div className="mt-4 pt-4 border-t border-white/10 space-y-2" data-testid="applicator-services-panel">
                  {(a.services || []).map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3">
                      <span className="text-sm">{s.service_name} <span className="text-xs text-gray-500">({s.category})</span></span>
                      <select value={s.cert_status} onChange={(e) => certify(a.applicator_id, s.service_id, e.target.value)} data-testid={`cert-select-${s.service_id}`}
                        className="bg-[#111] border border-white/15 px-2 py-1.5 text-xs">
                        {CERT.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  ))}
                  {!a.services?.length && <p className="text-xs text-gray-600">No services claimed.</p>}
                </div>
              )}
            </div>
          ))}
          {!apps.length && <p className="text-sm text-gray-600">No applicators.</p>}
        </div>
      </div>
    </div>
  );
}
